import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../api";

// Which address this page was opened on (task 2026-10-05-each-project-has-its-own-address).
//
// GET /api/address?host=<window.location.host> -> { enabled, base_url, kind, slug, host_label }
//   kind "base"    the portal itself (FRONTEND_URL)
//   kind "project" http://<host label>.<base host>/ — the data shell of project `slug`
//   kind "other"   any other name the portal is reached by: behaves as the portal
// `base_url` comes from the backend's FRONTEND_URL, never from this page's own host: it is the
// only place the SPA sends a person to sign in from a project host. The SPA never builds a
// project URL itself; the backend computes every one.
//
// A failed lookup (an old backend, the network) falls back to the portal: the base host must
// keep working whatever this feature does.

export const BASE_ADDRESS = { enabled: false, base_url: "", kind: "base", slug: null, host_label: null };

const AddressContext = createContext(BASE_ADDRESS);

export function useAddress() {
  return useContext(AddressContext);
}

export function AddressProvider({ value, children }) {
  return <AddressContext.Provider value={value || BASE_ADDRESS}>{children}</AddressContext.Provider>;
}

// Plain host names that can never be a project host: no lookup for them.
function obviouslyBase(hostname) {
  return !hostname || hostname === "localhost" || /^[\d.]+$/.test(hostname) || hostname.includes(":");
}

export function useAddressLookup() {
  const [state, setState] = useState(() => (obviouslyBase(window.location.hostname) ? BASE_ADDRESS : null));
  useEffect(() => {
    if (state) return undefined;
    let live = true;
    api.get(`/address?host=${encodeURIComponent(window.location.host)}`)
      .then(a => { if (live) setState({ ...BASE_ADDRESS, ...a }); })
      .catch(() => { if (live) setState(BASE_ADDRESS); });
    return () => { live = false; };
  }, [state]);
  return state;   // null while loading
}

// Leaving for another origin (the base host, or a project host). One seam, so tests can watch it.
export const leave = { to: url => window.location.assign(url) };

export const SLUG_RE = /^[a-z][a-z0-9_]{0,39}$/;
